import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Zap, Mail, User, Crown, Shield } from 'lucide-react';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <nav className="glass-heavy sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <Button data-testid="profile-back-dashboard" onClick={() => navigate('/dashboard')} variant="ghost" className="text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <h1 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Syne' }} data-testid="profile-title">Profile</h1>
        
        <div className="glass-card rounded-xl p-8 mb-6" data-testid="profile-card">
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                <User className="w-8 h-8" style={{ color: '#6366F1' }} />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Syne' }}>{user?.name}</h2>
              <p className="text-sm" style={{ color: '#94A3B8' }}>{user?.email}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <Crown className="w-5 h-5" style={{ color: user?.tier === 'premium' ? '#F59E0B' : '#475569' }} />
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Access</p>
                <p className="text-sm font-semibold">
                  {user?.tier === 'premium' ? 'Legacy unlimited access' : `${user?.simulation_credits || 0} paid credits`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <Shield className="w-5 h-5" style={{ color: '#6366F1' }} />
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Simulations Used</p>
                <p className="text-sm font-semibold">{user?.simulations_used || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {user?.tier === 'free' && (
          <div className="glass-card rounded-xl p-6 mb-6 flex items-center justify-between" style={{ border: '1px solid rgba(99,102,241,0.2)' }} data-testid="upgrade-prompt">
            <div>
              <p className="text-sm font-semibold" style={{ fontFamily: 'Syne' }}>Add decision credits</p>
              <p className="text-xs" style={{ color: '#94A3B8' }}>{user?.simulation_credits || 0} paid credits available · packs start at $4.99</p>
            </div>
            <Button data-testid="profile-upgrade-btn" onClick={() => navigate('/upgrade')} className="rounded-full px-4 text-xs" style={{ background: '#6366F1' }}>
              View Packs
            </Button>
          </div>
        )}

        <div className="glass-card rounded-xl p-6" data-testid="privacy-section">
          <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Syne' }}>Privacy & Data</h3>
          <p className="text-xs mb-4 leading-relaxed" style={{ color: '#94A3B8' }}>
            Your decision data is encrypted in transit and at rest. It is sent only to the AI providers named in our Privacy Policy to generate your result, and it is not sold for advertising. The product provides reflection prompts, not recommendations.
            For health, financial, legal, or safety-critical decisions, use it only to structure your questions and consult a qualified professional before acting.
          </p>
          <p className="text-xs" style={{ color: '#475569' }}>
            The Regret Engine is a simulation tool, not professional advice. Use it as one input among many in your decision-making process.
          </p>
        </div>

        <div className="mt-8">
          <Button data-testid="logout-profile-btn" onClick={handleLogout} variant="outline" className="rounded-full px-6 text-sm border-red-500/20 text-red-400 hover:bg-red-500/10">
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}
